// Utilitários para API de customização de serviços
import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();

// Constantes do serviço de contestação
export const DOCUMENT_CREATION_PRODUCT_CODES = {
  CONTESTACAO: "DC_CONTESTACAO",
  DOSSIE: "DC_CONTESTACAO_DOSSIE",
};

export const CONTESTATION_SERVICE = {
  CODE: "DOCUMENT_CREATION",
  ATTRIBUTES: {
    PROFILE_PARAMS: "PROFILE_PARAMS",
    RULES: "RULES",
    RULES_INTERNAL: "RULES_INTERNAL",
    RULES_SUBSIDIOS: "RULES_SUBSIDIOS",
    DOCX_TEMPLATE_FILE: "DOCX_TEMPLATE_FILE",
    REVIEW_REQUIRED: "REVIEW_REQUIRED",
    GENERATION_MODE: "MODO_GERACAO",
    /** Blob JSON: { RULES, RULES_INTERNAL, RULES_SUBSIDIOS } — específico do dossiê */
    DOSSIE_CELL_RULES: "DC_CONTESTACAO_DOSSIE",
    /** Blob JSON: mesma forma; leitura com fallback para chaves antigas (RULES, RULES_INTERNAL, RULES_SUBSIDIOS soltas) */
    CONTESTACAO_CELL_RULES: "DC_CONTESTACAO",
  },
};

/** Normaliza o valor de um blob de regras (DC_CONTESTACAO_* ) para { RULES, RULES_INTERNAL, RULES_SUBSIDIOS }. */
export function normalizeCellRulesBlob(raw) {
  if (raw == null || raw === "") {
    return { RULES: "", RULES_INTERNAL: "", RULES_SUBSIDIOS: "" };
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return normalizeCellRulesBlob(parsed);
    } catch {
      return { RULES: "", RULES_INTERNAL: "", RULES_SUBSIDIOS: "" };
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return {
      RULES: raw.RULES != null ? String(raw.RULES) : "",
      RULES_INTERNAL:
        raw.RULES_INTERNAL != null ? String(raw.RULES_INTERNAL) : "",
      RULES_SUBSIDIOS:
        raw.RULES_SUBSIDIOS != null ? String(raw.RULES_SUBSIDIOS) : "",
    };
  }
  return { RULES: "", RULES_INTERNAL: "", RULES_SUBSIDIOS: "" };
}

/** @deprecated use normalizeCellRulesBlob */
export const normalizeDossieCellRulesBlob = normalizeCellRulesBlob;

/**
 * Mescla blob DC_CONTESTACAO com o modelo antigo (atributos por chave).
 * Por campo: se a chave existir no objeto blob, usa o valor do blob; senão usa o flat.
 */
export function mergeContestationBlobWithFlat(rawBlob, flat) {
  const flatRules = flat.RULES != null ? String(flat.RULES) : "";
  const flatInternal =
    flat.RULES_INTERNAL != null ? String(flat.RULES_INTERNAL) : "";
  const flatSubsidios =
    flat.RULES_SUBSIDIOS != null ? String(flat.RULES_SUBSIDIOS) : "";

  if (rawBlob == null || rawBlob === "") {
    return {
      RULES: flatRules,
      RULES_INTERNAL: flatInternal,
      RULES_SUBSIDIOS: flatSubsidios,
    };
  }

  let o = rawBlob;
  if (typeof rawBlob === "string") {
    try {
      o = JSON.parse(rawBlob);
    } catch {
      return {
        RULES: flatRules,
        RULES_INTERNAL: flatInternal,
        RULES_SUBSIDIOS: flatSubsidios,
      };
    }
  }
  if (typeof o !== "object" || o === null || Array.isArray(o)) {
    return {
      RULES: flatRules,
      RULES_INTERNAL: flatInternal,
      RULES_SUBSIDIOS: flatSubsidios,
    };
  }

  const pick = (key, fallback) =>
    Object.prototype.hasOwnProperty.call(o, key)
      ? o[key] != null
        ? String(o[key])
        : ""
      : fallback;

  return {
    RULES: pick("RULES", flatRules),
    RULES_INTERNAL: pick("RULES_INTERNAL", flatInternal),
    RULES_SUBSIDIOS: pick("RULES_SUBSIDIOS", flatSubsidios),
  };
}

/** Lê regras da contestação: blob DC_CONTESTACAO com fallback para chaves legadas. */
export async function readContestationCellRulesAsObservations(token, cellId) {
  const pc = DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO;
  const [rawBlob, flatRules, flatSubsidios, flatInternal] = await Promise.all([
    readServiceCustomization(
      token,
      cellId,
      CONTESTATION_SERVICE.ATTRIBUTES.CONTESTACAO_CELL_RULES,
      pc,
    ),
    readServiceCustomization(
      token,
      cellId,
      CONTESTATION_SERVICE.ATTRIBUTES.RULES,
      pc,
    ),
    readServiceCustomization(
      token,
      cellId,
      CONTESTATION_SERVICE.ATTRIBUTES.RULES_SUBSIDIOS,
      pc,
    ),
    readServiceCustomization(
      token,
      cellId,
      CONTESTATION_SERVICE.ATTRIBUTES.RULES_INTERNAL,
      pc,
    ),
  ]);

  const merged = mergeContestationBlobWithFlat(rawBlob, {
    RULES: flatRules,
    RULES_INTERNAL: flatInternal,
    RULES_SUBSIDIOS: flatSubsidios,
  });

  return {
    client: merged.RULES,
    internal: merged.RULES_INTERNAL,
    subsidios: merged.RULES_SUBSIDIOS,
  };
}

/**
 * Grava apenas o blob DC_CONTESTACAO em customization_info e remove as chaves legadas
 * (RULES, RULES_SUBSIDIOS, RULES_INTERNAL) para não duplicar dado. Leitura continua com
 * mergeContestationBlobWithFlat para células ainda não migradas.
 */
export async function writeContestationCellRulesFromObservations(
  token,
  cellId,
  observations,
  hasInternalPermission,
) {
  const existing = await readContestationCellRulesAsObservations(token, cellId);
  const blob = {
    RULES: observations.client != null ? String(observations.client) : "",
    RULES_SUBSIDIOS:
      observations.subsidios != null ? String(observations.subsidios) : "",
    RULES_INTERNAL: hasInternalPermission
      ? observations.internal != null
        ? String(observations.internal)
        : ""
      : existing.internal != null
        ? String(existing.internal)
        : "",
  };

  const pc = DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO;
  const ok = await writeServiceCustomization(
    token,
    cellId,
    CONTESTATION_SERVICE.ATTRIBUTES.CONTESTACAO_CELL_RULES,
    blob,
    pc,
  );
  if (!ok) {
    return false;
  }
  await removeLegacyContestationFlatRuleKeys(
    token,
    cellId,
    hasInternalPermission,
    pc,
  );
  return true;
}

/** Remove atributos soltos antigos após consolidar em DC_CONTESTACAO. */
async function removeLegacyContestationFlatRuleKeys(
  token,
  cellId,
  hasInternalPermission,
  productCode = DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
) {
  await deleteServiceCustomization(
    token,
    cellId,
    CONTESTATION_SERVICE.ATTRIBUTES.RULES,
    productCode,
  );
  await deleteServiceCustomization(
    token,
    cellId,
    CONTESTATION_SERVICE.ATTRIBUTES.RULES_SUBSIDIOS,
    productCode,
  );
  if (hasInternalPermission) {
    await deleteServiceCustomization(
      token,
      cellId,
      CONTESTATION_SERVICE.ATTRIBUTES.RULES_INTERNAL,
      productCode,
    );
  }
}

/** Lê regras padrão do dossiê (schema DC_CONTESTACAO_DOSSIE) como { client, internal, subsidios }. */
export async function readDossieCellRulesAsObservations(token, cellId) {
  const raw = await readServiceCustomization(
    token,
    cellId,
    CONTESTATION_SERVICE.ATTRIBUTES.DOSSIE_CELL_RULES,
    DOCUMENT_CREATION_PRODUCT_CODES.DOSSIE,
  );
  const blob = normalizeDossieCellRulesBlob(raw);
  return {
    client: blob.RULES,
    internal: blob.RULES_INTERNAL,
    subsidios: blob.RULES_SUBSIDIOS,
  };
}

/**
 * Grava o blob DC_CONTESTACAO_DOSSIE. Sem permissão interna, preserva RULES_INTERNAL já salvo na célula.
 */
export async function writeDossieCellRulesFromObservations(
  token,
  cellId,
  observations,
  hasInternalPermission,
) {
  const currentRaw = await readServiceCustomization(
    token,
    cellId,
    CONTESTATION_SERVICE.ATTRIBUTES.DOSSIE_CELL_RULES,
    DOCUMENT_CREATION_PRODUCT_CODES.DOSSIE,
  );
  const current = normalizeDossieCellRulesBlob(currentRaw);
  const blob = {
    RULES: observations.client != null ? String(observations.client) : "",
    RULES_SUBSIDIOS:
      observations.subsidios != null ? String(observations.subsidios) : "",
    RULES_INTERNAL: hasInternalPermission
      ? observations.internal != null
        ? String(observations.internal)
        : ""
      : current.RULES_INTERNAL,
  };
  return writeServiceCustomization(
    token,
    cellId,
    CONTESTATION_SERVICE.ATTRIBUTES.DOSSIE_CELL_RULES,
    blob,
    DOCUMENT_CREATION_PRODUCT_CODES.DOSSIE,
  );
}

/**
 * Lê o valor de um atributo customizado
 */
export const readServiceCustomization = async (
  token,
  cellId,
  attributeKey,
  productCode,
) => {
  try {
    // Converter cell_id para número se necessário
    const cellIdNum = cellId ? (typeof cellId === 'string' ? parseInt(cellId, 10) : Number(cellId)) : null;
    
    if (!cellIdNum || isNaN(cellIdNum)) {
      return null;
    }
    
    const requestBody = {
      service_code: CONTESTATION_SERVICE.CODE,
      attribute_key: attributeKey,
      cell_id: cellIdNum,
      ...(productCode ? { product_code: productCode } : {}),
    };
    
    const response = await fetch(
      `${API_BASE_URL}/job/service_customization/read`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ServiceName: CONTESTATION_SERVICE.CODE,
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      // Se for 422 (Unprocessable Entity) ou 404, retornar null silenciosamente
      if (response.status === 422 || response.status === 404) {
        return null;
      }
      throw new Error(`Erro ao ler atributo: ${response.status}`);
    }

    const data = await response.json();
    return data.value;
  } catch (error) {
    // Retornar null silenciosamente em caso de erro
    return null;
  }
};

/**
 * Verifica se um atributo customizado existe
 */
export const checkServiceCustomizationExists = async (
  token,
  cellId,
  attributeKey,
  productCode,
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/job/service_customization/exists`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ServiceName: CONTESTATION_SERVICE.CODE,
        },
        body: JSON.stringify({
          service_code: CONTESTATION_SERVICE.CODE,
          attribute_key: attributeKey,
          cell_id: cellId,
          ...(productCode ? { product_code: productCode } : {}),
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Erro ao verificar existência: ${response.status}`);
    }

    const data = await response.json();
    return data.exists;
  } catch (error) {
    console.error(
      `Erro ao verificar existência do atributo ${attributeKey}:`,
      error
    );
    return false;
  }
};

/**
 * Escreve/atualiza o valor de um atributo customizado
 */
export const writeServiceCustomization = async (
  token,
  cellId,
  attributeKey,
  attributeValue,
  productCode,
) => {
  try {
    // Validar parâmetros obrigatórios
    if (!attributeKey) {
      throw new Error("attribute_key é obrigatório");
    }
    
    // Converter cell_id para número se necessário
    const cellIdNum = cellId ? (typeof cellId === 'string' ? parseInt(cellId, 10) : Number(cellId)) : null;
    
    if (!cellIdNum || isNaN(cellIdNum)) {
      throw new Error("cell_id é obrigatório e deve ser um número válido");
    }
    
    // Garantir que o valor seja serializado corretamente (booleano, string, objeto, etc)
    const requestBody = {
      service_code: CONTESTATION_SERVICE.CODE,
      attribute_key: String(attributeKey), // Garantir que seja string
      attribute_value: attributeValue,
      cell_id: cellIdNum,
      ...(productCode ? { product_code: productCode } : {}),
    };
    
    const response = await fetch(
      `${API_BASE_URL}/job/service_customization/write`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ServiceName: CONTESTATION_SERVICE.CODE,
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      throw new Error(`Erro ao salvar atributo: ${response.status}`);
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error(`Erro ao salvar atributo ${attributeKey}:`, error);
    return false;
  }
};

/**
 * Remove um atributo customizado
 */
export const deleteServiceCustomization = async (
  token,
  cellId,
  attributeKey,
  productCode,
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/job/service_customization/delete`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ServiceName: CONTESTATION_SERVICE.CODE,
        },
        body: JSON.stringify({
          service_code: CONTESTATION_SERVICE.CODE,
          attribute_key: attributeKey,
          cell_id: cellId,
          ...(productCode ? { product_code: productCode } : {}),
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Erro ao deletar atributo: ${response.status}`);
    }

    const data = await response.json();
    return data.deleted;
  } catch (error) {
    console.error(`Erro ao deletar atributo ${attributeKey}:`, error);
    return false;
  }
};

/**
 * Converte arquivo para base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove o prefixo "data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,"
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Converte base64 para blob para download
 */
export const base64ToBlob = (
  base64,
  mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Faz download de um arquivo a partir de base64
 */
export const downloadBase64File = (base64, filename) => {
  const blob = base64ToBlob(base64);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
